/**
 * What beat 7's cues mean in the hedgerow.
 *
 * The story owns the order of these moments (`src/story/act1/out.ts`) and
 * `main.ts` owns the renderer, the audio engine, physics and the sequencer. This
 * class owns the deterministic piece between them: two dogs, one breakable fence
 * bay, three authored cameras, the wear step — and **the bloom**, which is the
 * frame the whole act walks toward.
 *
 * ── The bloom is not this file's arithmetic, on purpose ──────────────────────
 *
 * `src/render/bloom.ts` owns the interpolation and all three of its readers (the
 * zone uniforms, the post stack's chroma ceiling, the sky and the exposure
 * reference). This file owns only *when* and *how long*. That split exists
 * because `docs/beat-7-plan.md` records the failure it prevents: a bloom written
 * against `applyZone()` alone warms the light and lifts the haze and **never
 * changes the saturation**, which would look nearly right in the one frame that
 * cannot afford to.
 *
 * **Render the palette, do not copy it.** There is no colour literal in this
 * file and there must never be one; two hard-coded colours in a director is how
 * the colour arc dies.
 *
 * ── Three cameras, and every one of them silently beats `shot()` ─────────────
 *
 * `framing()` returning non-null overrides an authored test camera on every
 * render frame, for as long as it returns non-null. That cost session 27 three
 * misdirected cameras in one sitting — a budget census, a shed wall, and all five
 * of a gate's review frames. So `cameraHeld` is exported, and any spec that wants
 * its own camera in this room **must wait on it being false**.
 *
 * ── The dogs live next door ──────────────────────────────────────────────────
 *
 * `src/world/outDogs.ts` owns both animals and the scent trail. Everything it
 * knows about the room arrives through its options, so it imports no room and no
 * story. What is left here is when they start working, when they stop being
 * patient, and when they give up at the bank.
 */

import * as THREE from 'three';
import { getWear, setWear } from '@/render/toon';
import type { Bloom, BloomCensus } from '@/render/bloom';
import { OUT_CUES } from '@/story/act1/out';
import type { Cue } from '@/story/types';
import { FadeOverlay } from '@/world/fadeOverlay';
import { OutDogs, type OutDogsDebug } from '@/world/outDogs';
import type { CoverVolume } from '@/systems/stealth';

/** The camera belongs to the beat for the first three seconds and then to the player. */
const OPEN_HOLD = 3;

/**
 * How long stage 2 stays patient before the dogs stop being patient.
 *
 * Seventy seconds, not the plan's seventy-five, and the five seconds are spent
 * where they do the most good. Capture is not instantaneous: the dogs have to
 * cross the yard at 2.6 m/s once they start driving, which is about four seconds
 * from where they are working. Two full captures therefore land at roughly 74 s
 * and 148 s, and `BOARDS_PATIENCE` (165 s, in `out.ts`) has to sit past the
 * second one with enough room left for a player to walk eight metres through a
 * bay that is now open. At 75 s the second capture lands at 158 and that margin
 * is four seconds; at 70 it is seventeen.
 *
 * The two numbers are one design and they are checked together by
 * `out-beat.spec.ts`'s no-input run, which is why this comment names the other.
 */
const CAPTURE_AFTER = 70;

/** Close enough to be taken. A dog's length (`DOG_SCALE.length` is 0.97). */
const CAPTURE_RANGE = 0.8;

/**
 * The mercy, and it is diegetic rather than mechanical.
 *
 * After the **second** capture the timber is already gone — a worker came through
 * the fence and did not fix it properly. Spec §5.6's thesis is that there is no
 * game over: the world gets harder, and she keeps going. This is the one place in
 * Act 1 where the anti-softlock backstop can be that instead of apologising for
 * it, so it is not a fade that does the run for her and it is not a prompt.
 *
 * **It is deliberately not one capture.** One is the beat teaching what capture
 * costs; two is the beat concluding that this player is not going to make the
 * roll, and neither reading survives if the fence opens the first time.
 */
const CAPTURES_BEFORE_MERCY = 2;

/**
 * Capture is a timeline, not a property of the fade, because the shared fade
 * converges exponentially. The same D-084 policy values beats 4 and 6 use.
 */
const CAPTURE = { blackAt: 2.5, backAt: 1.2 } as const;

/**
 * The bloom: seven seconds, eased, chroma last.
 *
 * `docs/beat-7-plan.md` rule 3. It runs against `OUT_HOLD` (16 s in `out.ts`), so
 * the frame is held for nine seconds *after* the colour has finished arriving.
 * That ratio is the shot — a bloom the camera cuts away from the moment it lands
 * is a transition, and this is not a transition.
 */
const BLOOM_SECONDS = 7;

/**
 * The wear step, and it **ramps rather than pops.**
 *
 * D-002 calls the wear channel *"the primary device that makes the plush read as
 * serious"* and `docs/story-review.md` S-4 files it here. The plan says she goes
 * into the water at one value and comes out at another; a step function would do
 * that in one frame, in front of a camera, which reads as a texture swap rather
 * than as a pig getting wet.
 *
 * Six seconds against stage 4's ten, so she is fully soaked with four seconds of
 * the ditch left. Driven on the **render** step, because wear is a uniform and
 * uniforms are presentation (D-045). The gate is a **delta** — `getWear()` before
 * the cue and after the ramp, and the difference asserted — because a level is
 * also what an unwired game reports, and that is exactly how this channel
 * survived five sessions of screenshot review.
 */
const WEAR_RAMP = 6;

/**
 * How far she has to go, in any direction, before the field lets her leave.
 *
 * Rule 4: the field has **no objective**, so the only thing the beat can honestly
 * wait for is the player deciding they have had enough. Going back toward the hedge
 * counts exactly as much as going on toward the road, and it is the more
 * interesting of the two.
 *
 * ── It is a DISPLACEMENT, and it was a pair of world-space thresholds ─────────
 *
 * `{ towardRoad: -62, backToHedge: -24 }`, and the no-input gate went red on the
 * first run: **stage 6 never appeared at all.** A player who presses nothing is
 * still standing in the ditch at z ≈ −19.2 when control comes back, which is
 * already past −24 on the hedge side, so `headingOut` was true on the stage's first
 * tick and the field was skipped in a single frame.
 *
 * The thresholds were written for a player who had walked out into the middle of
 * the field — which is where the plan's prose imagines her, and is not where the
 * simulation puts her. **An absolute position cannot express "she has moved"**
 * unless you already know where she started, so this measures from wherever control
 * was actually returned.
 *
 * Twelve metres is far enough that it cannot be a drift or a settle, and near
 * enough that a player heading anywhere with intent trips it in a few seconds. It
 * is also the only number in this beat a *stationary* player never reaches, which
 * is what leaves `FIELD_PATIENCE`'s 120 s as the real backstop rather than as
 * decoration.
 */
const FIELD_EXIT_DISTANCE = 12;

/**
 * The hand-over shot, and the one framing in this file that is a mirror.
 *
 * Beat 6 ends looking out from under the trailer at Kestner across the yard.
 * Beat 7 opens from the same place looking the other way — at the shed door,
 * where the boots are. It is the only hand-over in the game that needs to
 * establish nothing, because the player has been lying here for six seconds
 * already.
 */
const OPEN_CAMERA = { y: 0.30, lookY: 1.15, fov: 58 } as const;

/**
 * **The shot.** Hand-framed, static, and it does not follow her.
 *
 * Spec §7.7: *"Hand-framed static shot. Wet grass. The palette blooms into full
 * colour for the first time in the game. Hold it long."*
 *
 * Composition, and every number in it is a decision:
 *
 * - **fov 42**, which is the longest lens in the game. Every other camera here is
 *   52–58. A longer lens compresses distance, and compressed distance is what
 *   makes the road *present* rather than a line at infinity — Act 2's first
 *   sentence needs it in this frame.
 * - **0.40 m off the ground**, which is a hand's breadth above her. It puts the
 *   horizon high in frame, and a high horizon is most of what makes a sky read as
 *   enormous. It is also the height somebody kneeling in wet grass would hold a
 *   camera at, which is the register the spec asks for.
 * - **4.1 m from her**, so she subtends about a tenth of the frame's height. She
 *   is a figure in a landscape and not a portrait, because the sentence the shot
 *   has to deliver is *she is very small and the world is very large* — and
 *   because at a tenth of frame height the wear channel still reads, which is
 *   the other thing this shot is for.
 * - **Aimed past her**, into the field, so she enters the composition from the
 *   near left rather than sitting in the middle of it.
 *
 * **Do not add a sunbeam, a god ray, a lens flare or a bloom post effect.** The
 * bloom in this beat is a *palette* bloom. The post stack has paper grain, canvas
 * weave and outlines and that is this game's look; a light shaft would make this
 * shot look like every other game's shot, which is exactly what it must not do.
 */
const CUT_CAMERA = {
  from: { x: -8.2, dy: 0.40, z: -24.4 },
  /**
   * Aimed east-north-east, and the first version aimed the other way.
   *
   * The sun in this room is at −X, 17 degrees up (`hedgerowLighting`'s
   * `keyDirection`). The first aim was (−5.2, −28.0), which is mostly −Z with a
   * little +X — i.e. **turned away from the light**, so the frame got the pale
   * hemisphere on everything and the authored dawn gold appeared nowhere at all.
   *
   * The second attempt aimed at (−16, −32) — −X and −Z together, i.e. **straight
   * into the sun.** In a renderer with real rim light that is the most beautiful
   * dawn shot available; in this one it is the worst, because `TOON_RAMP` has three
   * soft bands and no rim term at all, so every surface facing the lens was its own
   * shadow side and the frame came back cooler than the one before it. Shooting
   * toward the light only works if the light can wrap an edge.
   *
   * (+3, −33) is the same idea with the sign fixed: −Z for the road, **+X so the
   * sun is behind the camera's left shoulder.** The faces in frame are the lit
   * ones, the low key rakes them at 17 degrees, and the shadows run away from the
   * lens down eighteen metres of field. Same camera position, same lens, one target
   * moved — and it is most of what makes the shot warm.
   */
  at: { x: 3, dy: 0.45, z: -33.0 },
  fov: 42,
} as const;

/**
 * The last shot in Act 1, and it is the only crane in the game.
 *
 * Spec §7.7: *"She does not celebrate; she looks back."* Delivered by the game
 * rather than by the player, which is the honest way round.
 *
 * **It is 3.2 m up, and it has to be.** From her own eye height, 0.25 m off a
 * field that has fallen away from the hedgerow, the 2.2 m hedge subtends 7.35°
 * and the shed's 3.4 m wall subtends 4.98° — **the hedge hides the shed
 * completely.** The thing she is looking back at is not visible from where she is
 * standing, and no amount of art direction fixes a sight line. Lifted to 3.2 m
 * the hedge falls to −1.62° and the shed rises to +1.44°, so the shed clears it.
 *
 * That constraint turns out to give the better shot, which is the usual way of
 * these things: at ~55 m the shed subtends about 3.5°, she at 6 m subtends about
 * 3.0°, and **the two are the same size in frame.** The thing she escaped is no
 * bigger than she is now. That was arithmetic before it was taste.
 *
 * **It is framed from HER position, not from a mark.** The room briefly exported a
 * `FIELD_MARK` for this and it was the wrong shape twice: stage 6 ends when she
 * crosses one of two thresholds sixty metres apart (`FIELD_EXIT`), so a fixed mark
 * would drag her *backwards* from wherever she actually got to — and rule 4's
 * whole claim is that the field has no place she is supposed to be. The camera
 * takes over where she is standing.
 *
 * Checked across the field's whole depth rather than at one point, because "her
 * position" now spans sixty metres. At her nearest exit (z ≈ −24) the hedge is at
 * −5.4° and the shed at +1.64°; at her furthest (z ≈ −62) the hedge is at −0.36°
 * and the shed at +1.26°. **The shed clears the hedge at both ends and the whole
 * way between**, and it gets smaller the further she went, which is the right
 * direction for it to move.
 *
 * If a frame shows the shed clipped by the hedge, the lever is `dy` and nothing
 * else — not the fov, and never the hedge.
 */
const LOOK_BACK_CAMERA = {
  /** Metres behind her (further into the field) and above the ground she is on. */
  behind: 6,
  dy: 3.2,
  /** The shed wall, mid-height. The one absolute in this file's cameras — see below. */
  at: { x: -1, y: 1.7, z: 10.6 },
  fov: 38,
} as const;

export interface Framing {
  position: { x: number; y: number; z: number };
  lookAt: { x: number; y: number; z: number };
  fov: number;
}

type Vec = { x: number; y: number; z: number };
type BoardApproach = {
  readonly x: number;
  readonly z: number;
  readonly facing: number;
  readonly speed: number;
  readonly rolling: boolean;
};

export interface OutDirectorOptions {
  /** The bloom, constructed by `main.ts` because it needs the post stack. */
  readonly bloom: Bloom;
  /** The room's cover volumes — cover from eyes IS shelter from rain (rule 1). */
  readonly cover: readonly CoverVolume[];
  /** The room's terrain. Nothing here may assume the ground is flat. */
  readonly groundY: (x: number, z: number) => number;
  /** Where the handler stands, at the shed door. */
  readonly handlerAt: { readonly x: number; readonly z: number };
  /** The near bank of the ditch. No dog ever goes past it. */
  readonly bankZ: number;
  /** The unique timber bay. The room supplies it so this director never imports room geometry. */
  readonly boardsAt: { readonly x: number; readonly z: number };
  /** The authored speed rule for breaking that bay, in metres per second. */
  readonly boardsBreakSpeed: number;
  /** Where she is put when a backstop puts her somewhere. */
  readonly underTrailerAt: { x: number; y: number; z: number; facing: number };
  readonly pastFenceAt: { x: number; y: number; z: number; facing: number };
  readonly inWaterAt: { x: number; y: number; z: number; facing: number };
  /** Where stage 7 stops her, so she can turn round. */
  readonly fieldMark: { readonly x: number; readonly z: number };
  /** Wear at the start of the beat and after the ditch. Both from `COLOUR_ARC`. */
  readonly wearFrom: number;
  readonly wearTo: number;

  /**
   * The boards give way: **the mesh swap and the collider removal in one call.**
   *
   * They are one event and this is the only place they can be wired apart, so
   * they are not two callbacks. `main.ts` is the only file with the physics world
   * and the scene, which is why it owns the pair rather than this class owning
   * half of it.
   */
  readonly onBoardsGo: () => void;

  /** One hit, and it is the loudest thing in Act 1. */
  readonly onBoardsBreak: (at: Vec) => void;
  /**
   * A roll arrived at the bay too slowly to break it: the timber takes it and
   * holds. `strength` is her speed over `boardsBreakSpeed`, 0..1, so the
   * answer grows as she gets closer to enough (D-209).
   */
  readonly onBoardsStrain: (strength: number) => void;
  /** Entry, then wading. */
  readonly onWater: (at: Vec) => void;
  /** He is calling somebody over. The same whistle that called the dog off in beat 4. */
  readonly onWhistle: (at: Vec) => void;
  /** A dog arrives at a trace and puts its nose down. An edge, not a level. */
  readonly onSnuff: (at: Vec) => void;
  /** Claws, and only on the apron. There are no claws on grass. */
  readonly onClaws: (at: Vec) => void;
  /** Whether she is under cover. A level, so every fixed step reports it. */
  readonly onSheltered: (sheltered: boolean) => void;
}

export class OutDirector {
  readonly #options: OutDirectorOptions;
  readonly #fadeOverlay = new FadeOverlay();
  readonly #dogs: OutDogs;

  #openHeld = false;
  #openElapsed = 0;

  #capturePatience: number | null = null;
  #captures = 0;
  #driving = false;
  #captureStage: 'out' | 'in' | null = null;
  #captureElapsed = 0;

  #boardsBroken = false;
  #placePastFence = false;
  #placeInWater = false;
  /** Rises on every capture. The controller consumes the edge; see `#stepCapture`. */
  #placeUnderTrailer = false;

  #cut = false;
  #bloomElapsed = 0;

  #wearElapsed: number | null = null;

  #fieldLive = false;
  /** Where control was returned. `FIELD_EXIT_DISTANCE` is measured from it. */
  #fieldFrom: { x: number; z: number } | null = null;
  #headingOut = false;

  #looksBack = false;
  /** Snapped from her own position when the last cue fires. See that cue. */
  #lookBackFrom: { x: number; z: number } | null = null;
  /** The last position the FIXED step reported. Written only in `step()`. */
  #lastSelf: Vec | null = null;
  #ended = false;

  constructor(scene: THREE.Object3D, options: OutDirectorOptions) {
    this.#options = options;
    this.#dogs = new OutDogs(scene, {
      cover: options.cover,
      handlerAt: options.handlerAt,
      groundY: options.groundY,
      bankZ: options.bankZ,
      onSnuff: options.onSnuff,
      onClaws: options.onClaws,
    });
    // The channel starts where beat 6 left it and steps at the ditch. `wearFrom`
    // is beat 6's row of `COLOUR_ARC` and `wearTo` is beat 7's; the table is the
    // value DURING a beat, and this beat is the one exception because it is the
    // beat the channel exists for. See `WEAR_RAMP`.
    setWear(options.wearFrom);
    // t = 0 once, here, so the pre-bloom grade is established before the first
    // frame rather than on whichever frame the first `update()` happens to be.
    // `Bloom`'s constructor deliberately writes nothing.
    options.bloom.apply(0);
  }

  get fade(): number {
    return this.#fadeOverlay.value;
  }

  /**
   * Everything the bloom drove on its last frame. Gate 1 reads this.
   *
   * Forwarded rather than re-derived, so the number a spec asserts is the number
   * that was written to the renderer. A director that recomputed the curve to
   * report it could pass a gate while `bloom.apply()` was never called at all —
   * which is the shape of failure `docs/beat-7-plan.md` names as the worst
   * available here.
   */
  get bloomCensus(): BloomCensus {
    return this.#options.bloom.census();
  }

  get dogs(): OutDogs {
    return this.#dogs;
  }

  /** She has decided she is done with the field. A world fact, not a cue. */
  get headingOut(): boolean {
    return this.#headingOut;
  }

  /** These rise once; callers use the edge to reset the body exactly once. */
  get placePastFence(): boolean {
    return this.#placePastFence;
  }

  get placeInWater(): boolean {
    return this.#placeInWater;
  }

  /**
   * Rises on each capture. **Unlike the other two this one repeats**, so its
   * consumer must clear its own latch rather than latch forever — see `main.ts`.
   */
  get placeUnderTrailer(): boolean {
    return this.#placeUnderTrailer;
  }

  /** Called by the consumer once it has actually moved her. */
  clearPlaceUnderTrailer(): void {
    this.#placeUnderTrailer = false;
  }

  get underTrailerAt(): { x: number; y: number; z: number; facing: number } {
    return this.#options.underTrailerAt;
  }

  get pastFenceAt(): { x: number; y: number; z: number; facing: number } {
    return this.#options.pastFenceAt;
  }

  get inWaterAt(): { x: number; y: number; z: number; facing: number } {
    return this.#options.inWaterAt;
  }

  /**
   * Whether the beat owns the camera. **Read this before placing one.**
   *
   * `framing()` beats `shot()` silently on every render frame. See the header.
   */
  get cameraHeld(): boolean {
    return this.framing() !== null;
  }

  get ended(): boolean {
    return this.#ended;
  }

  /**
   * Consume an actual rolling arrival at the timber bay.
   *
   * This runs immediately before the character controller asks Rapier to resolve
   * its next movement. Testing after that resolution reads a zero speed because
   * the intact collider has already stopped her, which was the shipped deadlock:
   * the threshold existed in the room and nothing could ever spend it. Keeping
   * the test here preserves the one `#breakBoards()` seam for mesh, collider and
   * sound while allowing the same player-driven tick to pass through the bay.
   */
  #strained = false;
  #strains = 0;

  tryBreakBoards(approach: BoardApproach): void {
    if (this.#boardsBroken) return;

    // The bay is 2 m wide and faces south. This is deliberately a short approach
    // band on its trailer side rather than a generic proximity test: rolling past
    // the fence, clipping a post, or approaching from the field must not remove it.
    const isAtBay = Math.abs(approach.x - this.#options.boardsAt.x) <= 1.2
      && approach.z >= this.#options.boardsAt.z
      && approach.z <= this.#options.boardsAt.z + 0.5;
    const rollingSouth = Math.cos(approach.facing) <= -0.7;
    const hit = isAtBay && rollingSouth && approach.rolling;
    if (hit && approach.speed >= this.#options.boardsBreakSpeed) {
      this.#breakBoards();
      return;
    }
    // Too slow: one strain per arrival, not one per tick she lies against it —
    // the band is left and re-entered by backing off for another go.
    if (hit && !this.#strained && approach.speed > 0.5) {
      this.#strained = true;
      this.#strains += 1;
      this.#options.onBoardsStrain(Math.min(1, approach.speed / this.#options.boardsBreakSpeed));
    }
    if (!isAtBay) this.#strained = false;
  }

  /** What the gates read instead of trying to infer a state from pixels. */
  get debug(): {
    bloom: number;
    wear: number;
    boardsBroken: boolean;
    /** Slow rolls the timber took and held. */
    boardStrains: number;
    captures: number;
    driving: boolean;
    cut: boolean;
    looksBack: boolean;
    headingOut: boolean;
    ended: boolean;
    dogs: OutDogsDebug;
  } {
    return {
      bloom: this.#options.bloom.progress,
      // Read back off the uniform rather than from a field, so the number the
      // gate asserts is the number the shader is using. A cached copy here would
      // pass a delta test on a `setWear` call that never reached the material.
      wear: this.#wear(),
      boardsBroken: this.#boardsBroken,
      boardStrains: this.#strains,
      captures: this.#captures,
      driving: this.#driving,
      cut: this.#cut,
      looksBack: this.#looksBack,
      headingOut: this.#headingOut,
      ended: this.#ended,
      dogs: this.#dogs.debug,
    };
  }

  handle(cue: Cue): void {
    if (this.#ended) return;
    switch (cue.id) {
      case OUT_CUES.open:
        this.#fadeOverlay.setTarget(0);
        this.#openHeld = true;
        break;

      case OUT_CUES.dogsWorkTheWall:
        this.#dogs.workTheWall();
        break;

      case OUT_CUES.offerBoards:
        // Nothing to do here: `main.ts` enables the two interactables, and both
        // of them are supposed to fail. D-043 says a verb the beat has not
        // offered must not be reachable; the corollary is that a verb it HAS
        // offered must be allowed to fail honestly — the timber shudders and
        // holds (D-209), and no stage listens. **That `main.ts` does enable
        // them is gated by `cold-spawn.spec.ts`'s beat 7 route**: until session
        // 71 this comment was the only thing that said so, and it was false.
        break;

      case OUT_CUES.armCapture:
        this.#dogs.startDepositing();
        this.#capturePatience = CAPTURE_AFTER;
        break;

      case OUT_CUES.boardsGo:
        this.#breakBoards();
        // The stage's own backstop fired, which means she is not through. Put her
        // through, the same shape beat 6's `putHerUnder` has.
        this.#placePastFence = true;
        this.#capturePatience = null;
        this.#driving = false;
        break;

      case OUT_CUES.theRun:
        // Nothing. **And that is the requirement, not an omission.** No cut, no
        // slow motion, no scripted line, no camera that knows where the ditch is.
        // The rig's own chase framing during a roll (§4: it pulls back and low)
        // is the whole of the presentation, and the quickest way to ruin the one
        // moment this game has been building its best verb toward is to help.
        this.#capturePatience = null;
        this.#driving = false;
        break;

      case OUT_CUES.driveHerIn:
        this.#placeInWater = true;
        break;

      case OUT_CUES.theDitch:
        this.#dogs.standDown();
        this.#wearElapsed = 0;
        this.#options.onWater({
          x: this.#options.inWaterAt.x,
          y: this.#options.inWaterAt.y + 0.1,
          z: this.#options.inWaterAt.z,
        });
        break;

      case OUT_CUES.theCut:
        this.#cut = true;
        this.#bloomElapsed = 0;
        break;

      case OUT_CUES.theField:
        this.#cut = false;
        this.#fieldLive = true;
        // Where control came back. Snapped once, from the fixed step's own last
        // position, because `FIELD_EXIT_DISTANCE` is measured from here and not
        // from anywhere in world space — see that constant.
        this.#fieldFrom = this.#lastSelf === null
          ? null
          : { x: this.#lastSelf.x, z: this.#lastSelf.z };
        break;

      case OUT_CUES.sheLooksBack:
        this.#fieldLive = false;
        this.#looksBack = true;
        // Snapped ONCE, here, from the last position the fixed step reported.
        //
        // The camera is static for the whole nine seconds — that is what "the
        // camera takes over" means — so it must not track her, and it must not be
        // recomputed per frame in `framing()`. Reading `#lastSelf` (a fixed-step
        // value) from a cue handler is safe because cues are drained inside the
        // fixed step, after `step()`; it is a render-step read of a fixed-step
        // value that would be the D-045 violation, and this is not one.
        this.#lookBackFrom = this.#lastSelf === null
          ? null
          : { x: this.#lastSelf.x, z: this.#lastSelf.z - LOOK_BACK_CAMERA.behind };
        break;

      case OUT_CUES.fadeOut:
        this.#fadeOverlay.setTarget(1);
        break;

      case OUT_CUES.end:
        // Beat 7 is the end of what is built, so disposal is not a lifecycle
        // guarantee — `disposeBeat` is wired to WebGL context loss and nothing
        // else, in every director in this project. D-087/D-090 therefore require
        // the fixed step itself to honour this flag, or two dogs go on walking
        // around behind a black screen indefinitely. Beat 5 shipped that bug.
        this.#ended = true;
        this.#driving = false;
        this.#capturePatience = null;
        this.#dogs.standDown();
        break;

      default:
        break;
    }
  }

  /** Fixed-step state only: the dogs, the capture clock, and her own thresholds. */
  step(dt: number, self: Vec): void {
    if (this.#ended) return;

    this.#lastSelf = { x: self.x, y: self.y, z: self.z };
    this.#dogs.step(dt, self);
    this.#stepCapturePatience(dt);
    this.#stepCapture(dt);
    this.#stepFieldExit(self);

    // A level, so it is reported every fixed step rather than on an edge. What it
    // drives is the rain mix — under steel the storm is a different sound — and a
    // level that is only sent on change goes stale the first time something else
    // resets the mixer.
    this.#options.onSheltered(this.#sheltered(self));
  }

  /**
   * Presentation catches up here and never writes anything the fixed step reads.
   *
   * The bloom, the wear ramp, the fade and the dogs' legs. All four are things
   * the player sees and nothing the simulation may branch on (D-045).
   */
  update(frameDt: number): void {
    const dt = Number.isFinite(frameDt) ? Math.max(0, Math.min(0.1, frameDt)) : 0;
    this.#dogs.update(dt);
    this.#fadeOverlay.update(dt);

    if (this.#openHeld) {
      this.#openElapsed += dt;
      if (this.#openElapsed >= OPEN_HOLD) this.#openHeld = false;
    }

    if (this.#cut) {
      this.#bloomElapsed += dt;
      // Clamped inside `Bloom`, and monotonic inside `Bloom`. Both belong there
      // rather than here: this file decides when the seven seconds start, and
      // `bloom.ts` is what guarantees the curve never runs backwards.
      this.#options.bloom.apply(this.#bloomElapsed / BLOOM_SECONDS);
    }

    if (this.#wearElapsed !== null) {
      this.#wearElapsed += dt;
      const t = Math.min(1, this.#wearElapsed / WEAR_RAMP);
      setWear(this.#options.wearFrom + (this.#options.wearTo - this.#options.wearFrom) * t);
      if (t >= 1) this.#wearElapsed = null;
    }
  }

  framing(): Framing | null {
    if (this.#openHeld) {
      const at = this.#options.underTrailerAt;
      return {
        position: { x: at.x, y: this.#options.groundY(at.x, at.z) + OPEN_CAMERA.y, z: at.z },
        // Out at the shed door, where the boots are. The mirror of the shot beat
        // 6 closed on.
        lookAt: {
          x: this.#options.handlerAt.x,
          y: OPEN_CAMERA.lookY,
          z: this.#options.handlerAt.z,
        },
        fov: OPEN_CAMERA.fov,
      };
    }

    if (this.#cut) {
      const from = CUT_CAMERA.from;
      const at = CUT_CAMERA.at;
      return {
        position: { x: from.x, y: this.#options.groundY(from.x, from.z) + from.dy, z: from.z },
        lookAt: { x: at.x, y: this.#options.groundY(at.x, at.z) + at.dy, z: at.z },
        fov: CUT_CAMERA.fov,
      };
    }

    if (this.#looksBack && this.#lookBackFrom !== null) {
      const from = this.#lookBackFrom;
      return {
        position: { x: from.x, y: this.#options.groundY(from.x, from.z) + LOOK_BACK_CAMERA.dy, z: from.z },
        // The one `lookAt` in this file with an ABSOLUTE y rather than a ground
        // offset, because what it is aimed at is a point on a wall fifty-five
        // metres away in a different room's terrain. Deriving it from
        // `groundY(10.6)` would be reaching into the yard's flat apron for a
        // height that belongs to the shed.
        lookAt: LOOK_BACK_CAMERA.at,
        fov: LOOK_BACK_CAMERA.fov,
      };
    }

    return null;
  }

  dispose(): void {
    this.#fadeOverlay.dispose();
    this.#dogs.dispose();
  }

  /**
   * Read back off the uniform, not off a field this class also writes.
   *
   * A cached copy here would pass gate 2's delta on a `setWear()` call that never
   * reached the material — which is precisely the failure mode that let this
   * channel sit unwired for six beats.
   */
  #wear(): number {
    return getWear();
  }

  #sheltered(self: Vec): boolean {
    for (const volume of this.#options.cover) {
      if (
        self.x >= volume.min.x && self.x <= volume.max.x
        && self.y >= volume.min.y && self.y <= volume.max.y
        && self.z >= volume.min.z && self.z <= volume.max.z
      ) return true;
    }
    return false;
  }

  #stepCapturePatience(dt: number): void {
    if (this.#capturePatience === null) return;

    if (this.#capturePatience > 0) {
      this.#capturePatience -= dt;
      if (this.#capturePatience > 0) return;
      // The dogs stop being patient. This is the world getting harder, and it is
      // the only place in the beat where they are told where she is rather than
      // working out where she has been.
      this.#driving = true;
      this.#dogs.drive();
      return;
    }

    if (!this.#driving || this.#captureStage !== null) return;
    if (this.#dogs.closestRange > CAPTURE_RANGE) return;

    this.#captures += 1;
    this.#options.onWhistle({
      x: this.#options.handlerAt.x,
      y: 1.62,
      z: this.#options.handlerAt.z,
    });
    this.#fadeOverlay.setTarget(1);
    this.#captureStage = 'out';
    this.#captureElapsed = 0;
  }

  #stepCapture(dt: number): void {
    if (this.#captureStage === null) return;
    this.#captureElapsed += dt;

    if (this.#captureStage === 'out' && this.#captureElapsed >= CAPTURE.blackAt) {
      // ── Put her back under the trailer, and the first build did NOT ──────────
      //
      // This line was `this.#placePastFence = false;` with a comment claiming
      // *"back under the trailer with the run reset"*. It cleared an internal flag
      // and moved nothing: `main.ts` only ever latches `placePastFence` and
      // `placeInWater`, each behind a `hedgerowPlaced*` boolean that never returns
      // to false, so a captured player was left standing exactly where the dogs
      // caught them — free to be caught again on the spot, with no run to reset.
      //
      // **The comment asserted a safety property the code did not have**, which is
      // D-097 in one line, and it survived because nothing exercised the capture
      // path. `out-beat.spec.ts` now plays through two captures.
      //
      // The re-place is a rising edge the controller consumes, exactly like beat
      // 6's `placeUnderTrailer`, because the controller is the only thing allowed
      // to move a body.
      this.#placeUnderTrailer = true;
      // And the two forward placements are re-armed, so a capture after one of them
      // has fired can still put her through or in.
      this.#placePastFence = false;
      this.#placeInWater = false;
      // The boards, if this was the second time, are already gone when the fade
      // comes back up. Behind black, so the change is never seen happening.
      if (this.#captures >= CAPTURES_BEFORE_MERCY) this.#breakBoards();
      // Reset both halves of pursuit while the screen is black. Clearing only
      // this director's `#driving` left OutDogs in its own driving state, so the
      // animals followed her to the respawn and the next frame was a surround.
      this.#dogs.resetAfterCapture();
      this.#fadeOverlay.setTarget(0);
      this.#captureStage = 'in';
      this.#captureElapsed = 0;
      this.#driving = false;
      this.#capturePatience = CAPTURE_AFTER;
      return;
    }

    if (this.#captureStage === 'in' && this.#captureElapsed >= CAPTURE.backAt) {
      this.#captureStage = null;
    }
  }

  #stepFieldExit(self: Vec): void {
    if (!this.#fieldLive || this.#headingOut) return;
    const from = this.#fieldFrom;
    if (from === null) return;
    if (Math.hypot(self.x - from.x, self.z - from.z) >= FIELD_EXIT_DISTANCE) {
      this.#headingOut = true;
    }
  }

  #breakBoards(): void {
    if (this.#boardsBroken) return;
    this.#boardsBroken = true;
    this.#options.onBoardsGo();
    this.#options.onBoardsBreak({
      x: this.#options.pastFenceAt.x,
      y: 0.6,
      z: 0,
    });
  }
}
